import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import '@/styles/ipe/workbench/ipe-workbench.css';

const DEFAULT_FILE_RULES = ['stamp', 'template', 'carousel'];
const DEFAULT_FOLDER_RULES = ['stamp', 'template'];

/**
 * 与主图创建列表一致的「下载选项」弹窗（命名规则、分文件夹、包含原图/底图）
 */
export default function IpeDownloadOptionsModal({ isOpen, onClose, onConfirm }) {
  const { t } = useTranslation('workbench');
  const [separator, setSeparator] = useState('');
  const [separateFolders, setSeparateFolders] = useState(false);
  const [includeOriginal, setIncludeOriginal] = useState(false);
  const [includeBase, setIncludeBase] = useState(false);
  const [fileNameRules, setFileNameRules] = useState(DEFAULT_FILE_RULES);
  const [folderNameRules, setFolderNameRules] = useState(DEFAULT_FOLDER_RULES);
  const [fixedFileName, setFixedFileName] = useState('');
  const [fixedFolderName, setFixedFolderName] = useState('');
  const [rulePanelType, setRulePanelType] = useState(null);
  const [rulePanelPos, setRulePanelPos] = useState({ top: 0, left: 0 });
  const [confirming, setConfirming] = useState(false);

  const rulePanelCloseTimerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setSeparator('');
    setSeparateFolders(false);
    setIncludeOriginal(false);
    setIncludeBase(false);
    setFileNameRules([...DEFAULT_FILE_RULES]);
    setFolderNameRules([...DEFAULT_FOLDER_RULES]);
    setFixedFileName('');
    setFixedFolderName('');
    setRulePanelType(null);
    setConfirming(false);
  }, [isOpen]);

  useEffect(
    () => () => {
      if (rulePanelCloseTimerRef.current) {
        clearTimeout(rulePanelCloseTimerRef.current);
      }
    },
    [],
  );

  const closePanels = useCallback(() => {
    setRulePanelType(null);
  }, []);

  const handleClose = useCallback(() => {
    if (confirming) return;
    closePanels();
    onClose?.();
  }, [closePanels, confirming, onClose]);

  const handleBackdropClick = useCallback(() => {
    handleClose();
  }, [handleClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleClose]);

  const openDownloadRulePanel = useCallback((type, anchorEl) => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    if (rulePanelCloseTimerRef.current) {
      clearTimeout(rulePanelCloseTimerRef.current);
      rulePanelCloseTimerRef.current = null;
    }
    setRulePanelType(type);
    setRulePanelPos({
      top: Math.round(rect.bottom + 6),
      left: Math.round(rect.left),
    });
  }, []);

  const scheduleCloseDownloadRulePanel = useCallback(() => {
    if (rulePanelCloseTimerRef.current) clearTimeout(rulePanelCloseTimerRef.current);
    rulePanelCloseTimerRef.current = setTimeout(() => {
      setRulePanelType(null);
    }, 120);
  }, []);

  const keepDownloadRulePanelOpen = useCallback(() => {
    if (rulePanelCloseTimerRef.current) {
      clearTimeout(rulePanelCloseTimerRef.current);
      rulePanelCloseTimerRef.current = null;
    }
  }, []);

  const buildSnapshot = useCallback(
    () => ({
      separator,
      separateFolders,
      fileNameRules,
      folderNameRules,
      fixedFileName,
      fixedFolderName,
      includeOriginal,
      includeBase,
    }),
    [
      separator,
      separateFolders,
      fileNameRules,
      folderNameRules,
      fixedFileName,
      fixedFolderName,
      includeOriginal,
      includeBase,
    ],
  );

  const handleConfirmClick = async () => {
    if (confirming || !onConfirm) return;
    setConfirming(true);
    try {
      await onConfirm(buildSnapshot());
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <React.Fragment>
      <div className="funding-modal funding-modal--above-selection-bar">
        <div
          className="funding-modal__backdrop"
          role="presentation"
          onClick={handleBackdropClick}
          onMouseEnter={scheduleCloseDownloadRulePanel}
        />
        <div
          className="funding-modal__panel funding-modal__panel--unified funding-modal__panel--download"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ipe-download-options-title"
          onClick={(event) => event.stopPropagation()}
          onMouseEnter={scheduleCloseDownloadRulePanel}
        >
          <header className="funding-modal__header">
            <h3 id="ipe-download-options-title">{t('downloadOptions.title')}</h3>
            <button type="button" className="funding-modal__close" onClick={handleClose} aria-label={t('downloadOptions.close')}>
              ×
            </button>
          </header>
          <div className="funding-modal__body">
            <div className="funding-download-options">
              <div className="funding-download-options__section">
                <h4 className="funding-download-options__title">{t('downloadOptions.fileNamingRule')}</h4>
                <div className="funding-download-options__naming">
                  {fileNameRules.length === 0 ? (
                    <div className="funding-download-options__naming-item">{t('downloadOptions.carouselName')}</div>
                  ) : (
                    fileNameRules.map((ruleKey, idx) => (
                      <React.Fragment key={`${ruleKey}-${idx}`}>
                        {idx > 0 && <div className="funding-download-options__naming-plus">+</div>}
                        <div className="funding-download-options__naming-item">
                          {ruleKey === 'fixed'
                            ? fixedFileName || t('downloadOptions.fixedName')
                            : ruleKey === 'stamp'
                              ? t('downloadOptions.stampFileName')
                              : ruleKey === 'template'
                                ? t('downloadOptions.templateName')
                                : t('downloadOptions.carouselName')}
                        </div>
                      </React.Fragment>
                    ))
                  )}
                  <div
                    className="funding-download-options__setting-wrap funding-download-options__setting-wrap--file"
                    onMouseEnter={(e) => openDownloadRulePanel('file', e.currentTarget)}
                    onMouseLeave={scheduleCloseDownloadRulePanel}
                  >
                    <button
                      type="button"
                      className="funding-download-options__setting-btn"
                      title={t('downloadOptions.currentSeparator', { value: separator === '' ? t('downloadOptions.none') : separator })}
                    >
                      {t('downloadOptions.settings')}
                    </button>
                  </div>
                </div>
              </div>

              <div className="funding-download-options__section">
                <div className="funding-download-options__checkbox-row">
                  <label className="funding-download-options__checkbox-label">
                    <input
                      type="checkbox"
                      checked={separateFolders}
                      onChange={(e) => setSeparateFolders(e.target.checked)}
                    />
                    <span>{t('downloadOptions.separateFolders')}</span>
                  </label>
                </div>
                {separateFolders && (
                  <>
                    <h4 className="funding-download-options__title">{t('downloadOptions.folderNamingRule')}</h4>
                    <div className="funding-download-options__naming">
                      {folderNameRules.length === 0 ? (
                        <div className="funding-download-options__naming-item">{t('downloadOptions.templateName')}</div>
                      ) : (
                        folderNameRules.map((ruleKey, idx) => (
                          <React.Fragment key={`${ruleKey}-${idx}`}>
                            {idx > 0 && <div className="funding-download-options__naming-plus">+</div>}
                            <div className="funding-download-options__naming-item">
                              {ruleKey === 'fixed'
                            ? fixedFolderName || t('downloadOptions.fixedName')
                            : ruleKey === 'stamp'
                              ? t('downloadOptions.stampFileName')
                              : ruleKey === 'template'
                                ? t('downloadOptions.templateName')
                                : t('downloadOptions.carouselName')}
                            </div>
                          </React.Fragment>
                        ))
                      )}
                      <div
                        className="funding-download-options__setting-wrap funding-download-options__setting-wrap--folder"
                        onMouseEnter={(e) => openDownloadRulePanel('folder', e.currentTarget)}
                        onMouseLeave={scheduleCloseDownloadRulePanel}
                      >
                        <button type="button" className="funding-download-options__setting-btn">
                          {t('downloadOptions.settings')}
                        </button>
                      </div>
                    </div>
                  </>
                )}
                <div className="funding-download-options__switch-row">
                  <span className="funding-download-options__switch-label">{t('downloadOptions.includeOriginal')}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={includeOriginal}
                    className={`funding-download-options__switch${includeOriginal ? ' is-on' : ''}`}
                    onClick={() => setIncludeOriginal((v) => !v)}
                  >
                    <span className="funding-download-options__switch-thumb" />
                  </button>
                </div>
                <div className="funding-download-options__switch-row">
                  <span className="funding-download-options__switch-label">{t('downloadOptions.includeBase')}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={includeBase}
                    className={`funding-download-options__switch${includeBase ? ' is-on' : ''}`}
                    onClick={() => setIncludeBase((v) => !v)}
                  >
                    <span className="funding-download-options__switch-thumb" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <footer className="funding-modal__footer funding-download-options-modal__footer">
            <button
              type="button"
              className="funding-download-options-modal__btn funding-download-options-modal__btn--cancel"
              disabled={confirming}
              onClick={handleClose}
            >
              {t('downloadOptions.cancel')}
            </button>
            <button
              type="button"
              className="funding-download-options-modal__btn funding-download-options-modal__btn--confirm"
              disabled={confirming}
              onClick={handleConfirmClick}
            >
              {confirming ? t('downloadOptions.packing') : t('downloadOptions.confirmDownload')}
            </button>
          </footer>
        </div>
      </div>
      {rulePanelType
        ? createPortal(
            <div
              className="funding-download-options__panel funding-download-options__panel--portal"
              style={{ top: `${rulePanelPos.top}px`, left: `${rulePanelPos.left}px` }}
              onMouseEnter={keepDownloadRulePanelOpen}
              onMouseLeave={scheduleCloseDownloadRulePanel}
            >
              <div className="funding-download-options__panel-title">
                {rulePanelType === 'file' ? t('downloadOptions.setFileRule') : t('downloadOptions.setFolderRule')}
              </div>
              <div className="funding-download-options__panel-tip">{t('downloadOptions.orderTip')}</div>
              {[
                { key: 'fixed', label: t('downloadOptions.fixedName') },
                { key: 'stamp', label: t('downloadOptions.stampFileName') },
                { key: 'template', label: t('downloadOptions.templateName') },
                { key: 'carousel', label: t('downloadOptions.carouselName') },
              ].map((option) => (
                <label key={option.key} className="funding-download-options__panel-check">
                  <input
                    type="checkbox"
                    checked={
                      rulePanelType === 'file'
                        ? fileNameRules.includes(option.key)
                        : folderNameRules.includes(option.key)
                    }
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (rulePanelType === 'file') {
                        setFileNameRules((prev) =>
                          checked
                            ? prev.includes(option.key)
                              ? prev
                              : [...prev, option.key]
                            : prev.filter((k) => k !== option.key),
                        );
                      } else {
                        setFolderNameRules((prev) =>
                          checked
                            ? prev.includes(option.key)
                              ? prev
                              : [...prev, option.key]
                            : prev.filter((k) => k !== option.key),
                        );
                      }
                    }}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
              {rulePanelType === 'file' && fileNameRules.includes('fixed') && (
                <input
                  type="text"
                  value={fixedFileName}
                  onChange={(e) => setFixedFileName(e.target.value)}
                  className="funding-download-options__fixed-input"
                  placeholder={t('downloadOptions.fixedNamePh')}
                />
              )}
              {rulePanelType === 'folder' && folderNameRules.includes('fixed') && (
                <input
                  type="text"
                  value={fixedFolderName}
                  onChange={(e) => setFixedFolderName(e.target.value)}
                  className="funding-download-options__fixed-input"
                  placeholder={t('downloadOptions.fixedNamePh')}
                />
              )}
              {rulePanelType === 'file' && (
                <div className="funding-download-options__panel-separator">
                  <span>{t('downloadOptions.separatorLabel')}</span>
                  <div className="funding-download-options__separator-picker">
                    <button
                      type="button"
                      className={`funding-download-options__sep-btn${separator === '' ? ' is-active' : ''}`}
                      onClick={() => setSeparator('')}
                    >
                      {t('downloadOptions.none')}
                    </button>
                    <button
                      type="button"
                      className={`funding-download-options__sep-btn${separator === '-' ? ' is-active' : ''}`}
                      onClick={() => setSeparator('-')}
                    >
                      -
                    </button>
                    <button
                      type="button"
                      className={`funding-download-options__sep-btn${separator === '+' ? ' is-active' : ''}`}
                      onClick={() => setSeparator('+')}
                    >
                      +
                    </button>
                  </div>
                </div>
              )}
            </div>,
            document.body,
          )
        : null}
    </React.Fragment>
  );
}
